import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit3, Trash2, Phone, Car, IdCard, Loader2 } from "lucide-react";
import { Card, Pill } from "@/components/layout/PageShell";
import MasterCrudShell from "@/components/layout/MasterCrudShell";
import { DataToolbar } from "@/components/layout/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

const AVAIL_TONE = {
  Available: "green",
  "On Delivery": "blue",
  "Off Duty": "default",
};

const EMPTY = {
  code: "",
  name: "",
  mobile: "",
  vehicle: "",
  license_no: "",
  availability: "Available",
  is_default: false,
  status: "Active",
};

export default function DeliveryPersonMaster() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const branchId = user?.default_branch?.id;
  const [view, setView] = useState("form");
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: drivers = [], isLoading } = useQuery({
    queryKey: ["delivery-persons", branchId],
    queryFn: async () => {
      const { data } = await api.get("/delivery-persons", { params: { branch_id: branchId } });
      return data.delivery_persons;
    },
    enabled: Boolean(branchId),
  });

  const filtered = drivers.filter((d) =>
    d.name.toLowerCase().includes(search.toLowerCase()) ||
    d.code.toLowerCase().includes(search.toLowerCase()),
  );

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        code: form.code,
        name: form.name,
        mobile: form.mobile || null,
        vehicle: form.vehicle || null,
        license_no: form.license_no || null,
        availability: form.availability,
        is_default: form.is_default,
        status: form.status,
      };
      if (editingId) {
        return api.put(`/delivery-persons/${editingId}`, payload, { params: { branch_id: branchId } });
      }
      return api.post("/delivery-persons", payload, { params: { branch_id: branchId } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["delivery-persons", branchId] });
      toast.success(editingId ? "Driver updated" : "Driver created");
      setEditingId(null);
      setForm(EMPTY);
      setView("form");
    },
    onError: () => toast.error("Failed to save driver"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/delivery-persons/${id}`, { params: { branch_id: branchId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["delivery-persons", branchId] });
      toast.success("Driver deleted");
    },
    onError: () => toast.error("Failed to delete driver"),
  });

  function openNew() {
    setEditingId(null);
    setForm({ ...EMPTY, code: `DRV-${String(drivers.length + 1).padStart(2, "0")}` });
  }

  function openEdit(d) {
    setEditingId(d.id);
    setForm({
      code: d.code,
      name: d.name,
      mobile: d.mobile || d.phone || "",
      vehicle: d.vehicle || "",
      license_no: d.license || "",
      availability: d.availability,
      is_default: d.is_default,
      status: d.status,
    });
    setView("form");
  }

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSave() {
    if (!form.code?.trim() || !form.name?.trim()) {
      toast.error("Code and name are required.");
      return;
    }
    saveMutation.mutate();
  }

  return (
    <MasterCrudShell
      title="Delivery Person Master"
      subtitle={`${drivers.length} drivers — ${user?.default_branch?.name || "branch"}`}
      view={view}
      onViewChange={setView}
      isNew={!editingId}
      onSave={handleSave}
      saving={saveMutation.isPending}
      onAddNew={openNew}
      testIdPrefix="drv"
      formTitle={editingId ? form.name || "Edit Driver" : "New Driver"}
      formSubtitle={editingId ? "Edit" : "Create"}
      list={
        <>
          <DataToolbar
            searchValue={search}
            onSearch={setSearch}
            testIdPrefix="drv"
          />
          {isLoading ? (
            <div className="h-32 grid place-items-center text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {filtered.map((d) => (
                <Card
                  key={d.id}
                  className="p-4 cursor-pointer hover:border-slate-300"
                  data-testid={`drv-card-${d.code}`}
                  onClick={() => openEdit(d)}
                >
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 grid place-items-center text-white font-bold text-[13px]">
                      {d.name.split(" ").map((s) => s[0]).join("").slice(0, 2)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-semibold text-slate-900 truncate">{d.name}</div>
                      <div className="text-[11.5px] text-slate-500 font-mono">{d.code}</div>
                    </div>
                    <Pill tone={AVAIL_TONE[d.availability] || "default"}>{d.availability}</Pill>
                  </div>
                  <div className="mt-3 space-y-1.5 text-[12px] text-slate-600">
                    <div className="flex items-center gap-2"><Phone size={12} className="text-slate-400" /> {d.phone || "—"}</div>
                    <div className="flex items-center gap-2"><Car size={12} className="text-slate-400" /> {d.vehicle || "—"}</div>
                    <div className="flex items-center gap-2"><IdCard size={12} className="text-slate-400" /> {d.license || "—"}</div>
                  </div>
                  {d.is_default && (
                    <Pill tone="amber" className="mt-2">Default Driver</Pill>
                  )}
                  <div className="mt-3 flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 flex-1 text-[12px]"
                      data-testid={`drv-edit-${d.id}`}
                      onClick={() => openEdit(d)}
                    >
                      <Edit3 size={12} className="mr-1.5" /> Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2 text-[12px] hover:text-red-600 hover:border-red-200"
                      data-testid={`drv-del-${d.id}`}
                      onClick={() => {
                        if (window.confirm(`Delete driver ${d.name}?`)) {
                          deleteMutation.mutate(d.id);
                        }
                      }}
                    >
                      <Trash2 size={12} />
                    </Button>
                  </div>
                </Card>
              ))}
              {!filtered.length && (
                <div className="col-span-full text-center text-slate-500 py-10 text-[13px]">
                  No delivery persons found.
                </div>
              )}
            </div>
          )}
        </>
      }
    >
      <div className="grid gap-3 max-w-md">
        <Field label="Code *">
          <Input
            value={form.code}
            onChange={(e) => setField("code", e.target.value)}
            className="h-9 font-mono"
            disabled={Boolean(editingId)}
            autoFocus={!editingId}
          />
        </Field>
        <Field label="Name *">
          <Input
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            className="h-9"
            autoFocus={Boolean(editingId)}
          />
        </Field>
        <Field label="Mobile">
          <Input value={form.mobile} onChange={(e) => setField("mobile", e.target.value)} className="h-9" />
        </Field>
        <Field label="Vehicle">
          <Input value={form.vehicle} onChange={(e) => setField("vehicle", e.target.value)} className="h-9" placeholder="Bike — DXB-A 12345" />
        </Field>
        <Field label="License No">
          <Input value={form.license_no} onChange={(e) => setField("license_no", e.target.value)} className="h-9 font-mono" />
        </Field>
        <Field label="Availability">
          <Select value={form.availability} onValueChange={(v) => setField("availability", v)}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Available">Available</SelectItem>
              <SelectItem value="On Delivery">On Delivery</SelectItem>
              <SelectItem value="Off Duty">Off Duty</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Status">
          <Select value={form.status} onValueChange={(v) => setField("status", v)}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <div className="flex items-center justify-between py-1">
          <Label className="text-[12.5px] text-slate-700">Default driver for branch</Label>
          <Switch checked={form.is_default} onCheckedChange={(v) => setField("is_default", v)} />
        </div>
      </div>
    </MasterCrudShell>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[12px] text-slate-600 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
